import { Component, Project, SampleFile, TextFile } from "projen";
import {
  AgentConfigOptions,
  AgentRuleBundle,
  ClaudeHookEntry,
  ClaudeHooksConfig,
  ClaudePermissionsConfig,
  ClaudeSettingsConfig,
  SourceTierExamples,
} from "./agent-config-options";
import { buildBuiltInBundles, ResolvedRuleConventions } from "./bundles";
import {
  buildAgentRegistryRule,
  buildConventionsRegistryRule,
  bundleNameForWorkflowRule,
  isSuppressedWorkflowRule,
} from "./bundles/agent-registry";
import { resolveBuildPolicy } from "./bundles/build-policy";
import { hasAnyDownstreamIssueKindBundle } from "./bundles/bundle-ownership";
import {
  renderCustomDocSections,
  renderSourceTierExamples,
} from "./bundles/features";
import { renderFocusSection } from "./bundles/focus";
import {
  resolveIssueDefaults,
  validateIssueDefaultsConfig,
} from "./bundles/issue-defaults";
import {
  collectIssueTemplateRecipeStubs,
  issueTemplatesGeneratedPath,
  renderIssueTemplateLabelsCheckerScript,
  renderIssueTemplatesBundleHook,
  renderIssueTemplatesCheckerScript,
  renderIssueTemplatesGeneratedPage,
  renderIssueTemplatesStarterPage,
  resolveIssueTemplates,
  validateIssueTemplatesConfig,
} from "./bundles/issue-templates";
import { renderMeetingTypesSection } from "./bundles/meeting-types";
import { resolveOrchestratorAssets } from "./bundles/orchestrator";
import { resolveAgentPaths, ResolvedAgentPaths } from "./bundles/paths";
import {
  resolvePrReviewPolicy,
  validatePrReviewPolicyConfig,
} from "./bundles/pr-review-policy";
import { renderPriorityRulesSection } from "./bundles/priority-rules";
import {
  renderProgressFilesBundleHook,
  resolveProgressFiles,
  validateProgressFilesConfig,
} from "./bundles/progress-files";
import { resolveRunRatio } from "./bundles/run-ratio";
import {
  renderScheduledTaskSkillFile,
  validateScheduledTasksConfig,
} from "./bundles/scheduled-tasks";
import { scopeFilePatternsToProjects } from "./bundles/scope";
import { validateScopeGateConfig } from "./bundles/scope-gate";
import {
  renderSharedEditingBundleHook,
  renderSharedEditingHelperScript,
  resolveSharedEditing,
  validateSharedEditingConfig,
} from "./bundles/shared-editing";
import {
  renderSkillEvalsBundleHook,
  renderSkillEvalsRunnerScript,
  resolveSkillEvals,
  validateSkillEvalsConfig,
} from "./bundles/skill-evals";
import {
  renderTemporalFramingCheckerScript,
  resolveTemporalFraming,
  validateTemporalFramingConfig,
} from "./bundles/temporal-framing";
import { validateAgentTierConfig } from "./bundles/tiers";
import { validateUnblockDependentsConfig } from "./bundles/unblock-dependents";
import { ClaudeRenderer } from "./renderers/claude-renderer";
import { CursorRenderer } from "./renderers/cursor-renderer";
import { resolveTemplateVariables } from "./template-resolver";
import { ProjectMetadata } from "../projects/project-metadata";
import { ResolvedProjectMetadata } from "../projects/project-metadata-options";
import { CodexRenderer } from "./renderers/codex-renderer";
import { CopilotRenderer } from "./renderers/copilot-renderer";
import {
  AGENT_MODEL,
  AGENT_PLATFORM,
  AGENT_RULE_SCOPE,
  AgentCommand,
  AgentModel,
  AgentPlatform,
  AgentProcedure,
  AgentRule,
  AgentSkill,
  AgentSubAgent,
} from "./types";

/**
 * Map a string tier value (`'powerful' | 'balanced' | 'fast'`) to
 * its corresponding `AgentModel`. Returns BALANCED when the input
 * is `undefined` or any unrecognised value so demote-target agents
 * default to Sonnet rather than throwing.
 *
 * Shared by `resolveDefaultAgentTier` (global knob) and
 * `resolveBundleAgentTiers` (per-bundle override map) so both
 * paths normalise identically.
 *
 * @internal
 */
function mapAgentTier(value: string | undefined): AgentModel {
  switch (value) {
    case "powerful":
      return AGENT_MODEL.POWERFUL;
    case "fast":
      return AGENT_MODEL.FAST;
    case "balanced":
    default:
      return AGENT_MODEL.BALANCED;
  }
}

/**
 * Resolve the project's default sub-agent model tier. Used to thread the
 * caller-configured `defaultAgentTier` knob into every analyst / writer /
 * profile / research / regulatory / standards / business-models / customer /
 * industry / meeting / maintenance bundle factory. Returns BALANCED when the
 * option is omitted so demote-target agents land on Sonnet by default.
 *
 * The four reviewer/orchestrator agents (orchestrator, issue-worker,
 * pr-reviewer, requirements-reviewer) ignore this resolver and stay pinned
 * on POWERFUL inside their bundle source.
 *
 * @internal
 */
export function resolveDefaultAgentTier(
  options?: AgentConfigOptions,
): AgentModel {
  return mapAgentTier(options?.defaultAgentTier);
}

/**
 * Bundle names that read the `bundleAgentTiers` per-bundle override
 * map. Any key in `bundleAgentTiers` that is not in this set is
 * rejected at synth time as a typo / invalid bundle name.
 *
 * Mirrors the demote-set asserted in
 * `bundles.test.ts#defaultAgentTier` — the two lists must stay in
 * lockstep.
 *
 * @internal
 */
export const TIER_AWARE_BUNDLE_NAMES: ReadonlyArray<string> = [
  "company-profile",
  "customer-profile",
  "maintenance-audit",
  "meeting-analysis",
  "people-profile",
  "software-profile",
];

/**
 * Resolve the project's per-bundle agent-tier override map. Returns
 * a `Map<bundleName, AgentModel>` containing only the bundles a
 * consumer pinned via `bundleAgentTiers`; bundles not present in
 * the map fall back to `defaultAgentTier` at lookup time inside
 * `buildBuiltInBundles`.
 *
 * Throws at synth time when the map contains a key that is not
 * one of the six tier-aware bundle names. Silently ignoring an
 * unknown key would let typos hide.
 *
 * @internal
 */
export function resolveBundleAgentTiers(
  options?: AgentConfigOptions,
): ReadonlyMap<string, AgentModel> {
  const overrides = options?.bundleAgentTiers;
  if (!overrides) {
    return new Map();
  }
  const validNames = new Set(TIER_AWARE_BUNDLE_NAMES);
  const out = new Map<string, AgentModel>();
  for (const [name, value] of Object.entries(overrides)) {
    if (!validNames.has(name)) {
      throw new Error(
        `bundleAgentTiers contains unknown bundle name "${name}". ` +
          `Valid names are: ${TIER_AWARE_BUNDLE_NAMES.join(", ")}.`,
      );
    }
    out.set(name, mapAgentTier(value));
  }
  return out;
}

/*******************************************************************************
 *
 * Default Claude Code permissions — hardened baseline that minimises approval
 * prompts while blocking destructive and dangerous operations.
 *
 * Projects can extend these via `claudeSettings.permissions.allow` /
 * `.deny` — user-supplied entries are appended (not replaced).
 *
 ******************************************************************************/

/** @internal */
const DEFAULT_CLAUDE_ALLOW: ReadonlyArray<string> = [
  // ── Git ──────────────────────────────────────────────────────────────
  "Bash(git add *)",
  "Bash(git branch *)",
  "Bash(git checkout *)",
  "Bash(git commit *)",
  "Bash(git diff *)",
  "Bash(git fetch *)",
  "Bash(git log *)",
  "Bash(git merge *)",
  "Bash(git mv *)",
  "Bash(git pull *)",
  "Bash(git push *)",
  "Bash(git rebase *)",
  "Bash(git rm *)",
  "Bash(git stash *)",
  "Bash(git status *)",
  "Bash(git show *)",
  "Bash(git rev-parse *)",

  // ── GitHub CLI ───────────────────────────────────────────────────────
  "Bash(gh issue *)",
  "Bash(gh pr *)",
  "Bash(gh repo *)",
  "Bash(gh api *)",
  "Bash(gh label *)",
  "Bash(gh run *)",
  "Bash(gh search *)",
  "Bash(gh browse *)",
  "Bash(gh status *)",

  // ── Package manager ──────────────────────────────────────────────────
  "Bash(pnpm *)",

  // ── Read-only shell utilities ────────────────────────────────────────
  "Bash(ls *)",
  "Bash(find *)",
  "Bash(cat *)",
  "Bash(head *)",
  "Bash(tail *)",
  "Bash(wc *)",
  "Bash(grep *)",
  "Bash(sort *)",
  "Bash(uniq *)",
  "Bash(dirname *)",
  "Bash(basename *)",
  "Bash(which *)",
  "Bash(diff *)",
  "Bash(jq *)",
  "Bash(date *)",

  // ── Safe output / test utilities ─────────────────────────────────────
  "Bash(echo *)",
  "Bash(printf *)",
  "Bash(test *)",
  "Bash([ *)",
  "Bash(true *)",
  "Bash(false *)",

  // ── Safe directory operations ────────────────────────────────────────
  "Bash(mkdir *)",
  "Bash(rmdir *)",

  // ── Built-in tools ───────────────────────────────────────────────────
  "Read(/**)",
  "Edit(/**)",
  "Write(/**)",
  "WebFetch",
  "WebSearch",
];

/** @internal */
const DEFAULT_CLAUDE_DENY: ReadonlyArray<string> = [
  // ── Destructive git ──────────────────────────────────────────────────
  "Bash(git push --force *)",
  "Bash(git push -f *)",
  "Bash(git push origin --force *)",
  "Bash(git push origin -f *)",
  "Bash(git reset --hard *)",
  "Bash(git clean -f *)",
  "Bash(git remote *)",

  // ── Destructive file operations ──────────────────────────────────────
  "Bash(rm -rf *)",
  "Bash(rm -r *)",
  "Bash(rm *)",

  // ── Network / remote access ──────────────────────────────────────────
  "Bash(curl *)",
  "Bash(wget *)",
  "Bash(ssh *)",
  "Bash(scp *)",

  // ── System administration ────────────────────────────────────────────
  "Bash(sudo *)",
  "Bash(chmod *)",
  "Bash(chown *)",
  "Bash(kill *)",
  "Bash(killall *)",
  "Bash(pkill *)",

  // ── Code execution / shell spawning ──────────────────────────────────
  "Bash(eval *)",
  "Bash(exec *)",
  "Bash(source *)",
  "Bash(. *)",
  "Bash(bash *)",
  "Bash(sh *)",
  "Bash(zsh *)",

  // ── App launching ────────────────────────────────────────────────────
  "Bash(open *)",
  "Bash(xdg-open *)",

  // ── Environment manipulation ─────────────────────────────────────────
  "Bash(export *)",
  "Bash(env *)",
];

/**
 * Path-based deny defaults — block read access to large generated trees,
 * lockfiles, and secret-bearing files. These complement the command-based
 * `DEFAULT_CLAUDE_DENY` baseline above by guarding the
 * `Read` tool surface area; an unfiltered `Read(/**)` allow line is fine
 * when paired with these denies because path denies take precedence over
 * tool-level allows.
 *
 * Entries are intentionally relative-to-cwd (`./.astro/**`) so they
 * match a project-rooted Read call without leaking onto sibling clones
 * the operator may explicitly point Claude at.
 *
 * @internal
 */
const DEFAULT_CLAUDE_PATH_DENY: ReadonlyArray<string> = [
  "Read(./.astro/**)",
  "Read(./.env)",
  "Read(./.env.*)",
  "Read(./*.lock)",
  "Read(./package-lock.json)",
];

/**
 * Tier-0 measurement file appended to by the default `Stop` hook.
 * Surfaced as an exported constant so the gitignore wiring inside the
 * `AgentConfig` component can reference the same path the hook writes
 * to without drift.
 *
 * @internal
 */
const DEFAULT_USAGE_LOG_PATH = ".claude/usage.log";

/**
 * Default environment variables shipped to every consumer's
 * `.claude/settings.json`. Consumer-supplied entries on
 * `claudeSettings.env` override the defaults on key collision.
 *
 * - **`ENABLE_TOOL_SEARCH`** — gates Claude Code's deferred-tool-loading
 *   optimization. With `"1"`, MCP tool schemas load on demand via a
 *   `ToolSearch` step instead of being pinned into the system prompt.
 *   This recovers per-turn context tokens when several MCP servers are
 *   connected. See knowledge-base technique 02 (`techniques/02-mcp-servers.md`)
 *   for details.
 *
 * The merger in `mergeClaudeDefaults` spreads these entries first and
 * then layers consumer entries on top, so a consumer that sets the
 * same key wins.
 *
 * @internal
 */
const DEFAULT_CLAUDE_ENV: Readonly<Record<string, string>> = {
  ENABLE_TOOL_SEARCH: "1",
};

/**
 * Default Claude Code hooks shipped to every consumer's
 * `.claude/settings.json` unless `claudeSettings.disableAllHooks: true`
 * is set.
 *
 * Claude Code delivers hook data as a JSON object on stdin (fields
 * such as `session_id` and `tool_input.file_path`), not as `CLAUDE_*`
 * environment variables, so both hooks parse their inputs out of that
 * stdin payload with `jq`.
 *
 * - **Stop** — reads `.session_id` from the stdin JSON and appends
 *   `<timestamp>,<session-id>` to `.claude/usage.log` on every Stop
 *   event so consumers can track per-session usage without a separate
 *   measurement harness. Claude Code does not pass token counts to
 *   hooks, so usage.log is a 2-column `<timestamp>,<session-id>` record
 *   — the earlier input/output-token columns are dropped. Disable by
 *   either setting `disableAllHooks: true` (drops every default and
 *   consumer-supplied hook) or by overriding the `Stop` array on
 *   `claudeSettings.hooks` — user-supplied entries are preserved
 *   alongside the defaults.
 * - **PostToolUse** — matches `Edit|Write`, reads `.tool_input.file_path`
 *   from the stdin JSON and, when the touched file lives under
 *   `docs/src/content/docs/`, runs `strip-tool-artifact-tags.sh` to
 *   remove any leaked tool-call wrapper closing tags from the end of
 *   the file (#779). Wrapped in a `case` shell statement that no-ops
 *   cleanly on a missing path or a missing procedure script (the
 *   stripper is `-x`-guarded), so the hook never blocks an edit. The
 *   stripper is idempotent, always exits 0, and produces no output that
 *   reaches the model.
 *
 * The merger in `mergeClaudeDefaults` appends these entries after any
 * consumer-supplied entries and dedupes by `(matcher, command)` so
 * defaults never collide with a consumer's identical override.
 *
 * @internal
 */
const DEFAULT_CLAUDE_HOOKS: ClaudeHooksConfig = {
  Stop: [
    {
      matcher: "*",
      hooks: [
        {
          type: "command",
          // Claude Code delivers hook data as a JSON object on stdin, not
          // as CLAUDE_* env vars, so read the session id from `.session_id`
          // via jq. Token counts are not passed to hooks, so the two token
          // columns are dropped — usage.log is now `<timestamp>,<session-id>`.
          command: `sid="$(jq -r '.session_id // "unknown"' 2>/dev/null)"; echo "$(date -u +%FT%TZ),\${sid:-unknown}" >> ${DEFAULT_USAGE_LOG_PATH}`,
        },
      ],
    },
  ],
  PostToolUse: [
    {
      matcher: "Edit|Write",
      hooks: [
        {
          type: "command",
          // Claude Code delivers hook data as a JSON object on stdin, not
          // as CLAUDE_* env vars, so read the edited file path from
          // `.tool_input.file_path` via jq. On files under
          // docs/src/content/docs/ this runs only the tool-artifact-tag
          // stripper (#779); it is `-x`-guarded so it no-ops cleanly when
          // the path is empty or the procedure script is not shipped.
          command:
            'path="$(jq -r \'.tool_input.file_path // empty\' 2>/dev/null)"; case "$path" in *docs/src/content/docs/*) if [ -x .claude/procedures/strip-tool-artifact-tags.sh ]; then bash .claude/procedures/strip-tool-artifact-tags.sh "$path" >/dev/null 2>&1; fi ;; esac',
        },
      ],
    },
  ],
};

/**
 * Phased-agent bundle rules that receive a "Progress File" hook
 * section in their rendered content when the progress-file convention
 * is enabled. The tuple is `[ruleName, agentLabel]`; the label is
 * substituted into the hook's opening sentence
 * (e.g. `"Every bcm-writer session writes a progress file …"`).
 *
 * Bundle rule names that do not exist in the current bundle
 * composition are silently skipped — consumers that opt out of a
 * phased-agent bundle do not need to also opt out of the hook.
 *
 * @internal
 */
const PROGRESS_FILE_BUNDLE_HOOKS: ReadonlyArray<readonly [string, string]> = [
  ["bcm-writer-workflow", "bcm-writer"],
  ["business-models-workflow", "business-models"],
  ["company-profile-workflow", "company-profile"],
  ["customer-profile-workflow", "customer-profile"],
  ["industry-discovery-workflow", "industry-discovery"],
  ["meeting-agenda-workflow", "agenda"],
  ["meeting-processing-workflow", "meeting-analysis"],
  ["regulatory-research-workflow", "regulatory-research"],
  ["requirements-analyst-workflow", "requirements-analyst"],
  ["research-pipeline-workflow", "research-pipeline"],
  ["software-profile-workflow", "software-profile"],
  ["standards-research-workflow", "standards-research"],
];

/**
 * Phased-agent bundle rules that receive a "Shared Index Editing"
 * hook section in their rendered content when the shared-editing
 * convention is enabled. The tuple is `[ruleName, agentLabel]`; the
 * label is substituted into the hook's opening sentence (e.g.
 * `"Every company-profile run that adds a row to a shared registry …"`).
 *
 * These are the bundles whose output documents are catalogued in a
 * shared `index.md` / `README.md` registry or a feature-matrix file,
 * plus the `requirements-reviewer` deprecation phase that edits an
 * existing registry row — the set that actually produces
 * concurrent-write contention on shared indexes. Bundle rule names
 * that do not exist in the current bundle composition are silently
 * skipped, so consumers that opt out of a phased-agent bundle do not
 * need to also opt out of the hook.
 *
 * @internal
 */
const SHARED_EDITING_BUNDLE_HOOKS: ReadonlyArray<readonly [string, string]> = [
  ["bcm-writer-workflow", "bcm-writer"],
  ["business-models-workflow", "business-models"],
  ["company-profile-workflow", "company-profile"],
  ["customer-profile-workflow", "customer-profile"],
  ["industry-discovery-workflow", "industry-discovery"],
  ["meeting-agenda-workflow", "agenda"],
  ["meeting-processing-workflow", "meeting-analyst"],
  ["people-profile-workflow", "people-profile"],
  ["regulatory-research-workflow", "regulatory-research"],
  ["requirements-reviewer-workflow", "requirements-reviewer"],
  ["requirements-writer-workflow", "requirements-writer"],
  ["research-pipeline-workflow", "research-pipeline"],
  ["software-profile-workflow", "software-profile"],
  ["standards-research-workflow", "standards-research"],
];

/**
 * Skill-owning bundle rules that receive a "Skill Evals" hook section
 * in their rendered content when the skill-evals convention is
 * enabled. The tuple is `[ruleName, skillLabel]`; the label is both
 * the on-disk skill-directory name under `<skillsRoot>/` and the
 * substitution used in the hook's opening sentence (e.g.
 * `"The write-requirement skill ships a regression suite at .claude/skills/write-requirement/evals/evals.json …"`).
 *
 * The list is reconciled against the skills that actually ship an
 * `evals/evals.json` file on disk — registering a skill that has no
 * eval suite would render a hook pointing at a missing path, which
 * undermines the convention. When a new skill grows an eval suite,
 * add the matching `[ruleName, skillLabel]` pair here; when a suite
 * is retired, remove the pair.
 *
 * Orchestration-only skills (e.g. review-pr, orchestrator
 * housekeeping loops) are deliberately excluded even if evals land
 * later — their outputs are actions rather than prose, and a
 * regression in those skills surfaces as a workflow failure rather
 * than an output mismatch.
 *
 * Bundle rule names that do not exist in the current bundle
 * composition are silently skipped, so consumers that opt out of a
 * skill-owning bundle do not need to also opt out of the hook.
 *
 * @internal
 */
const SKILL_EVALS_BUNDLE_HOOKS: ReadonlyArray<readonly [string, string]> = [
  ["company-profile-workflow", "profile-company"],
  ["requirements-analyst-workflow", "requirements-analyst"],
  ["requirements-writer-workflow", "write-requirement"],
];

/**
 * Phased-agent bundle rules that receive an "Issue Templates" hook
 * section in their rendered content when the issue-templates
 * convention is enabled. The tuple is `[ruleName, bundleLabel]`; the
 * label is substituted into the hook's opening sentence (e.g.
 * `"Every downstream gh issue create invocation dispatched by the company-profile bundle must …"`).
 *
 * The list targets every bundle whose phased workflow dispatches
 * downstream issues via `gh issue create` — the agents for whom a
 * canonical template reference meaningfully reduces drift. Pure
 * orchestration bundles (orchestrator, pr-review) do not file
 * downstream issues themselves, so their workflow rules do not
 * receive the hook; they triage and route existing issues rather
 * than minting new ones.
 *
 * Bundle rule names that do not exist in the current bundle
 * composition are silently skipped — consumers that opt out of a
 * phased-agent bundle do not need to also opt out of the hook.
 *
 * @internal
 */
const ISSUE_TEMPLATES_BUNDLE_HOOKS: ReadonlyArray<readonly [string, string]> = [
  ["bcm-writer-workflow", "bcm-writer"],
  ["business-models-workflow", "business-models"],
  ["company-profile-workflow", "company-profile"],
  ["customer-profile-workflow", "customer-profile"],
  ["industry-discovery-workflow", "industry-discovery"],
  ["maintenance-audit-workflow", "maintenance-audit"],
  ["meeting-agenda-workflow", "agenda"],
  ["meeting-processing-workflow", "meeting-analysis"],
  ["people-profile-workflow", "people-profile"],
  ["regulatory-research-workflow", "regulatory-research"],
  ["requirements-analyst-workflow", "requirements-analyst"],
  ["requirements-reviewer-workflow", "requirements-reviewer"],
  ["requirements-writer-workflow", "requirements-writer"],
  ["research-pipeline-workflow", "research-pipeline"],
  ["software-profile-workflow", "software-profile"],
  ["standards-research-workflow", "standards-research"],
];

/**
 * Generates AI coding assistant configuration files from a common schema.
 *
 * Supports Cursor and Claude Code (initial release), with Codex and Copilot
 * renderers planned for future issues. Rules, skills, and sub-agents are
 * defined once and rendered into the correct format for each target platform.
 *
 * Follows the configulator component pattern: extends Component, static .of()
 * factory, options interface with JSDoc.
 *
 * @example
 * ```ts
 * new AgentConfig(project, {
 *   rules: [{
 *     name: 'my-rule',
 *     description: 'Project conventions',
 *     scope: AGENT_RULE_SCOPE.ALWAYS,
 *     content: '# My Rule\n\nFollow these conventions...',
 *   }],
 * });
 * ```
 */
export class AgentConfig extends Component {
  /**
   * Find the AgentConfig component on a project.
   */
  public static of(project: Project): AgentConfig | undefined {
    const isAgentConfig = (c: Component): c is AgentConfig =>
      c instanceof AgentConfig;
    return project.components.find(isAgentConfig);
  }

  /**
   * Returns `true` when at least one tier array on the supplied
   * `SourceTierExamples` is non-empty, signalling that the consuming
   * repo has opted into rendering the base bundle's
   * `source-quality-verification` rule. Returns `false` for
   * `undefined`, `{}`, or a fully-empty `{ t1: [], t2: [], t3: [], t4: [] }`.
   */
  private static hasActiveTierExamples(
    examples: SourceTierExamples | undefined,
  ): boolean {
    if (!examples) {
      return false;
    }
    return (
      (examples.t1?.length ?? 0) > 0 ||
      (examples.t2?.length ?? 0) > 0 ||
      (examples.t3?.length ?? 0) > 0 ||
      (examples.t4?.length ?? 0) > 0
    );
  }

  /**
   * Merges default Claude permissions and hooks with bundle and
   * user-supplied settings.
   *
   * Permission merge order: defaults → bundle permissions → user-supplied
   * entries. Both `allow` and `deny` are deduped via
   * `Array.from(new Set(...))`; V8 `Set` iteration preserves insertion
   * order, so the final ordering is defaults first, then bundle, then
   * user, with duplicates removed (first-occurrence wins). After the
   * merge+dedupe, any entry whose value exactly matches a string in
   * `permissions.excludeDefaults` is dropped from `allow` / `deny` / `ask`
   * — the only supported way to remove a baseline default, since the merge
   * is otherwise append-only. `excludeDefaults` is a build-time directive
   * and is stripped from the rendered `permissions` object. `defaultMode`
   * is opt-in (steering decision D7): it is set only from
   * `userSettings.defaultMode` and left undefined otherwise, so the
   * renderer omits the key for un-opted-in consumers — see the inline
   * comment on the literal below.
   *
   * Hooks merge: consumer-supplied entries first, then default entries
   * (Stop, PostToolUse), deduped by `(matcher, JSON-serialized hooks)`.
   * Defaults are skipped entirely when `disableAllHooks: true` is set —
   * the `disableAllHooks` flag passes through to the rendered file so
   * Claude Code suppresses every project-level hook at runtime, and the
   * defaults are dropped at synth time so the rendered file does not
   * carry orphan entries that would re-fire if the operator later flipped
   * the flag back off.
   *
   * Env merge: defaults from `DEFAULT_CLAUDE_ENV` (e.g.
   * `ENABLE_TOOL_SEARCH=1`) layer first, then consumer-supplied
   * `claudeSettings.env` entries override on key collision. Sibling keys
   * from both sources land in the rendered file.
   */
  private static mergeClaudeDefaults(
    userSettings?: ClaudeSettingsConfig,
    bundlePermissions?: { allow: Array<string>; deny: Array<string> },
  ): ClaudeSettingsConfig {
    const bundleAllow = bundlePermissions?.allow ?? [];
    const bundleDeny = bundlePermissions?.deny ?? [];
    const userAllow = userSettings?.permissions?.allow ?? [];
    const userDeny = userSettings?.permissions?.deny ?? [];

    const exclude = new Set(userSettings?.permissions?.excludeDefaults ?? []);
    const dropExcluded = (entries: Array<string>): Array<string> =>
      entries.filter((entry) => !exclude.has(entry));

    // `excludeDefaults` is a build-time directive, not a rendered field.
    const { excludeDefaults: _excludeDefaults, ...passthroughPermissions } =
      userSettings?.permissions ?? {};

    // Defensive/forward-compat: no default `ask` entries exist today, but
    // filter any consumer-supplied ones for consistency. Only override the
    // passthrough `ask` when there is something to filter so the rendered
    // output is byte-identical for consumers that set no `excludeDefaults`.
    const askOverride = passthroughPermissions.ask?.length
      ? { ask: dropExcluded([...passthroughPermissions.ask]) }
      : {};

    const mergedPermissions: ClaudePermissionsConfig = {
      ...passthroughPermissions,
      allow: dropExcluded(
        Array.from(
          new Set([...DEFAULT_CLAUDE_ALLOW, ...bundleAllow, ...userAllow]),
        ),
      ),
      deny: dropExcluded(
        Array.from(
          new Set([
            ...DEFAULT_CLAUDE_DENY,
            ...DEFAULT_CLAUDE_PATH_DENY,
            ...bundleDeny,
            ...userDeny,
          ]),
        ),
      ),
      ...askOverride,
    };

    return {
      ...userSettings,
      // `defaultMode` is opt-in (steering decision D7): the base
      // output does NOT set it unless the consumer supplies
      // `claudeSettings.defaultMode`. When it is undefined the renderer
      // omits the key entirely, so an un-opted-in consumer's rendered
      // settings carry no `defaultMode`. Consumers that run autonomous
      // scheduled-task workers (issue-worker, orchestrator, pr-reviewer,
      // and the analyst/writer family) — which would deadlock on
      // confirmation prompts — opt in by setting
      // `claudeSettings.defaultMode: "dontAsk"`.
      defaultMode: userSettings?.defaultMode,
      permissions: mergedPermissions,
      hooks: AgentConfig.mergeClaudeHooks(userSettings),
      env: { ...DEFAULT_CLAUDE_ENV, ...(userSettings?.env ?? {}) },
    };
  }

  /**
   * Merge default lifecycle hooks (Stop, PostToolUse) with consumer-
   * supplied entries on `claudeSettings.hooks`. Consumer entries appear
   * first so a downstream override that wires a faster lint/format hook
   * runs ahead of the defaults; defaults are appended after.
   *
   * Returns `undefined` when neither defaults nor consumer entries
   * remain — the renderer skips the `hooks` key entirely in that case
   * so opt-out repos do not ship an empty `"hooks": {}` object.
   *
   * Defaults are gated on `disableAllHooks !== true`. When the flag is
   * set we still pass through any consumer-supplied entries (the
   * runtime-level `disableAllHooks: true` is what suppresses execution),
   * but we never inject the bundle defaults. That keeps the disable-all
   * escape hatch idempotent: flipping the flag drops the bundle's
   * default surface area instead of leaving the entries on disk for a
   * future re-enable.
   */
  private static mergeClaudeHooks(
    userSettings?: ClaudeSettingsConfig,
  ): ClaudeHooksConfig | undefined {
    const userHooks = userSettings?.hooks;
    const includeDefaults = userSettings?.disableAllHooks !== true;
    const defaults = includeDefaults ? DEFAULT_CLAUDE_HOOKS : {};

    const allEvents = new Set<string>([
      ...Object.keys(userHooks ?? {}),
      ...Object.keys(defaults),
    ]);

    const merged: Record<string, ReadonlyArray<ClaudeHookEntry>> = {};
    for (const event of allEvents) {
      const userEntries =
        (
          (userHooks ?? {}) as Record<
            string,
            ReadonlyArray<ClaudeHookEntry> | undefined
          >
        )[event] ?? [];
      const defaultEntries =
        (
          defaults as Record<string, ReadonlyArray<ClaudeHookEntry> | undefined>
        )[event] ?? [];

      const seen = new Set<string>();
      const combined: Array<ClaudeHookEntry> = [];
      for (const entry of [...userEntries, ...defaultEntries]) {
        const key = JSON.stringify({
          matcher: entry.matcher,
          hooks: entry.hooks,
        });
        if (seen.has(key)) continue;
        seen.add(key);
        combined.push(entry);
      }

      if (combined.length > 0) {
        merged[event] = combined;
      }
    }

    return Object.keys(merged).length > 0
      ? (merged as ClaudeHooksConfig)
      : undefined;
  }

  private readonly options: AgentConfigOptions;
  private cachedBundles?: ReadonlyArray<AgentRuleBundle>;
  private cachedPaths?: ResolvedAgentPaths;

  constructor(project: Project, options: AgentConfigOptions = {}) {
    super(project);
    this.options = options;
  }

  /**
   * Resolved agent-path roots for this project. Consumer overrides on
   * `AgentConfigOptions.paths` cascade into derived roots via
   * `resolveAgentPaths()`. Memoized so repeated reads during synthesis
   * do not re-run the resolver.
   */
  private get resolvedPaths(): ResolvedAgentPaths {
    if (!this.cachedPaths) {
      this.cachedPaths = resolveAgentPaths(this.options.paths);
    }
    return this.cachedPaths;
  }

  /**
   * Path-aware built-in bundles assembled with this project's
   * resolved paths. Path-aware bundle factories (e.g.
   * `buildResearchPipelineBundle`) receive the resolved struct so
   * their rendered rule content reflects any consumer override.
   * Bundles that do not read agent paths are passed through as-is
   * from their default const exports.
   *
   * The build policy is auto-detected from the project's `TurboRepo`
   * component here rather than configured, so build guidance in the
   * `github-workflow` and `turborepo` rules only claims an AWS
   * credential requirement when a remote cache actually exists. The
   * getter is lazy by design — `TurboRepo` must already be attached
   * to the project when the bundles are first read.
   *
   * Every **config-driven convention rule** is likewise resolved here
   * and seeded into its owning bundle, so the rule enters the rule map
   * already carrying the consumer's settings. Rewriting those rules
   * after the map was assembled — the previous approach — silently
   * discarded any `ruleExtensions` append or same-name `rules`
   * override that had already been merged in.
   */
  private get pathAwareBundles(): ReadonlyArray<AgentRuleBundle> {
    if (!this.cachedBundles) {
      this.cachedBundles = buildBuiltInBundles(
        this.resolvedPaths,
        resolveIssueDefaults(this.options.issueDefaults),
        resolveDefaultAgentTier(this.options),
        resolveBundleAgentTiers(this.options),
        resolvePrReviewPolicy(this.options.prReviewPolicy),
        resolveBuildPolicy(this.project),
        this.resolvedRuleConventions,
      );
    }
    return this.cachedBundles;
  }

  /**
   * Resolved settings for every config-driven convention rule, derived
   * from this project's options. Consumed by `buildBuiltInBundles` so
   * the `base` and `orchestrator` bundles seed final rule content.
   *
   * `excludeBundles` feeds two of these: the orchestrator's rendered
   * tier table / scope-gate overrides / scheduled-tasks registry drop
   * rows owned by excluded bundles, and the issue-templates rule falls
   * back to its disabled stub once every downstream-issue-kind bundle
   * has been excluded.
   */
  private get resolvedRuleConventions(): ResolvedRuleConventions {
    const excludeBundles = this.options.excludeBundles ?? [];
    const orchestratorAssets = resolveOrchestratorAssets(
      this.options.tiers,
      this.options.scopeGate,
      this.options.runRatio,
      this.options.scheduledTasks,
      this.options.unblockDependents,
      excludeBundles,
    );

    return {
      base: {
        progressFiles: resolveProgressFiles(this.options.progressFiles),
        sharedEditing: resolveSharedEditing(this.options.sharedEditing),
        temporalFraming: resolveTemporalFraming(this.options.temporalFraming),
        skillEvals: resolveSkillEvals(this.options.skillEvals),
        issueTemplates: resolveIssueTemplates(this.options.issueTemplates),
        hasDownstreamIssueKindBundles:
          hasAnyDownstreamIssueKindBundle(excludeBundles),
      },
      orchestrator: {
        tiers: orchestratorAssets.tiers,
        scopeGate: orchestratorAssets.scopeGate,
        runRatio: orchestratorAssets.runRatio,
        scheduledTasks: orchestratorAssets.scheduledTasks,
        unblockDependents: orchestratorAssets.unblockDependents,
        excludeBundles,
      },
    };
  }

  /**
   * Returns the bundles that are active for this project: auto-detected
   * bundles (when `autoDetectBundles !== false`) plus force-included
   * bundles, minus explicitly excluded bundles. Deduplicated by name.
   *
   * Exposed so sibling components (e.g. the sync-labels workflow) can
   * consume bundle-contributed configuration.
   */
  public get activeBundles(): ReadonlyArray<AgentRuleBundle> {
    const bundleMap = new Map<string, AgentRuleBundle>();

    if (this.options.autoDetectBundles !== false) {
      for (const bundle of this.pathAwareBundles) {
        if (this.options.excludeBundles?.includes(bundle.name)) {
          continue;
        }
        if (bundle.name === "base" && this.options.includeBaseRules === false) {
          continue;
        }
        if (bundle.appliesWhen(this.project)) {
          bundleMap.set(bundle.name, bundle);
        }
      }
    }

    if (this.options.includeBundles) {
      for (const bundleName of this.options.includeBundles) {
        const bundle = this.pathAwareBundles.find((b) => b.name === bundleName);
        if (bundle) {
          bundleMap.set(bundle.name, bundle);
        }
      }
    }

    return [...bundleMap.values()];
  }

  public override preSynthesize(): void {
    super.preSynthesize();

    // Validate the tier config **before** any rendering so a malformed
    // `AgentTierConfig` fails the build with a clear error instead of
    // silently shipping broken sort keys. Throwing here stops
    // synthesis before any file is written.
    validateAgentTierConfig(this.options.tiers);

    // Validate the scope-gate config at the same time. Malformed
    // thresholds (negative, non-integer, or inverted) fail the build
    // before rendering so the orchestrator never ships a gate that
    // would mis-classify issues.
    validateScopeGateConfig(this.options.scopeGate);

    // Validate the PR review policy config. Empty / whitespace-only
    // entries in `prReviewPolicy.autoMerge.pathsExemptFromSize` fail
    // the build before rendering so the rendered policy YAML never
    // ships with a broken carve-out glob.
    validatePrReviewPolicyConfig(this.options.prReviewPolicy);

    // Validate the run-ratio config. Malformed ratios (non-integer
    // or non-positive) or an empty / absolute state-file path fail
    // the build before rendering so the orchestrator never ships a
    // broken housekeeping cadence. Resolving here also gives us the
    // canonical state-file path so we can gitignore it — the rendered
    // CLAUDE.md claims the file is gitignored in consumer repos, so
    // the bundle is responsible for making that claim true.
    const resolvedRunRatio = resolveRunRatio(this.options.runRatio);
    if (resolvedRunRatio.enabled) {
      this.project.gitignore.addPatterns(`/${resolvedRunRatio.stateFilePath}`);
    }

    // Gitignore the per-session usage-log file written by the default
    // `Stop` hook. The default hook is opt-out via
    // `claudeSettings.disableAllHooks`, so the gitignore entry is only
    // added when the hook will actually fire — flipping the flag drops
    // both the hook and the gitignore line so the file pattern never
    // outlives the writer.
    if (this.options.claudeSettings?.disableAllHooks !== true) {
      this.project.gitignore.addPatterns(`/${DEFAULT_USAGE_LOG_PATH}`);
    }

    // Validate the agent-driven unblock-dependents config. Empty /
    // whitespace-only comment templates fail the build before
    // rendering so the emitted unblock-dependents.sh never ships
    // with a broken comment body.
    validateUnblockDependentsConfig(this.options.unblockDependents);

    // Validate the per-phase-label issue-defaults overrides. Unknown
    // status:* / priority:* values, empty entries, and empty
    // phase-label keys fail the build before rendering so bundle
    // prompts never ship with a malformed `gh issue create` recipe.
    validateIssueDefaultsConfig(this.options.issueDefaults);

    // Validate the progress-file convention. Empty / absolute
    // stateDir, filename patterns missing the <ISSUE_NUMBER>
    // placeholder, unknown format values, and non-positive
    // staleAfterHours fail the build before rendering so agents
    // never ship with a broken resume contract. Resolving here also
    // gives us the canonical state directory so we can gitignore it
    // — the rendered convention claims progress files never land on
    // the default branch, so the bundle is responsible for making
    // that claim true.
    const resolvedProgressFiles = validateProgressFilesConfig(
      this.options.progressFiles,
    );
    if (resolvedProgressFiles.enabled) {
      this.project.gitignore.addPatterns(`/${resolvedProgressFiles.stateDir}/`);
    }

    // Validate the scheduled-tasks config. Unknown model values,
    // empty taskId / agent / typeLabel fields, overrides keyed on an
    // unknown taskId, and duplicate taskIds inside `tasks` fail the
    // build before rendering so the bundle never ships a broken
    // per-agent worker SKILL.md.
    const resolvedScheduledTasks = validateScheduledTasksConfig(
      this.options.scheduledTasks,
      this.options.excludeBundles ?? [],
    );

    // Emit one .claude/scheduled-tasks/<taskId>/SKILL.md per enabled
    // task. The subsystem is opt-in at the task level: every default
    // entry ships with `enabled: false`, so repos that never touch
    // `options.scheduledTasks` see no files on disk. Consumers flip
    // specific tasks on via `overrides.<taskId>.enabled = true`.
    if (resolvedScheduledTasks.enabled) {
      for (const task of resolvedScheduledTasks.tasks) {
        if (!task.enabled) continue;
        const filePath = `${resolvedScheduledTasks.root}/${task.taskId}/SKILL.md`;
        new TextFile(this, filePath, {
          lines: renderScheduledTaskSkillFile(task).split("\n"),
        });
      }
    }

    // Validate the shared-editing convention. Empty / whitespace-only
    // path entries and unknown conflictStrategy values fail the build
    // before rendering so agents never ship with a malformed
    // shared-index contract. The optional helper script is emitted
    // here when the consumer opts in — keeping the emission next to
    // validation keeps every shared-editing side-effect in one place.
    const resolvedSharedEditing = validateSharedEditingConfig(
      this.options.sharedEditing,
    );
    if (resolvedSharedEditing.enabled && resolvedSharedEditing.emitHelper) {
      new TextFile(this, ".claude/procedures/verify-index-row.sh", {
        lines: renderSharedEditingHelperScript(resolvedSharedEditing).split(
          "\n",
        ),
        executable: true,
      });
    }

    // Validate the skill-evals convention. Empty / whitespace-only or
    // absolute `skillsRoot` / `productContextPath` fail the build
    // before rendering so agents never ship with a malformed eval
    // harness contract. The optional runner script is emitted here
    // when the consumer opts in — keeping the emission next to
    // validation keeps every skill-evals side-effect in one place.
    const resolvedSkillEvals = validateSkillEvalsConfig(
      this.options.skillEvals,
    );
    if (resolvedSkillEvals.enabled && resolvedSkillEvals.emitRunner) {
      new TextFile(this, ".claude/procedures/run-skill-evals.sh", {
        lines: renderSkillEvalsRunnerScript(resolvedSkillEvals).split("\n"),
        executable: true,
      });
    }

    // Validate the issue-templates convention. Empty / whitespace-only
    // or absolute `templatesPath` and empty / whitespace-only pattern
    // entries fail the build before rendering so agents never ship
    // with a malformed templates contract. The optional lint script
    // and starter doc are emitted here when the consumer opts in —
    // keeping the emission next to validation keeps every
    // issue-templates side-effect in one place.
    const resolvedIssueTemplates = validateIssueTemplatesConfig(
      this.options.issueTemplates,
    );
    if (resolvedIssueTemplates.enabled) {
      if (resolvedIssueTemplates.emitChecker) {
        new TextFile(this, ".claude/procedures/check-issue-templates.sh", {
          lines: renderIssueTemplatesCheckerScript(
            resolvedIssueTemplates,
          ).split("\n"),
          executable: true,
        });
        // Companion lint: `check-issue-templates.sh` polices *where*
        // recipes live, this one polices *what they say* — that every
        // `## Template:` recipe pairs its phase label with the
        // `type:<bundle>` label the bundle-ownership map requires and
        // assigns a GitHub issue type.
        new TextFile(
          this,
          ".claude/procedures/check-issue-template-labels.sh",
          {
            lines: renderIssueTemplateLabelsCheckerScript(
              resolvedIssueTemplates,
            ).split("\n"),
            executable: true,
          },
        );
      }
      if (resolvedIssueTemplates.emitStarterDoc) {
        // SampleFile: write-once — the starter page scaffolds the
        // hand-authored templates file without clobbering it on later
        // projen runs. Subsequent `pnpm exec projen` runs leave the
        // file alone so authors can flesh out the scaffold.
        new SampleFile(this.project, resolvedIssueTemplates.templatesPath, {
          contents: renderIssueTemplatesStarterPage(resolvedIssueTemplates),
        });
        // TextFile: always regenerated. A `SampleFile` never reaches a
        // consumer whose templates page already exists, so the label
        // half of every recipe lives here instead — one
        // correct-by-construction stub per phase label the active
        // bundles emit, refreshed on every synth.
        new TextFile(
          this,
          issueTemplatesGeneratedPath(resolvedIssueTemplates.templatesPath),
          {
            lines: renderIssueTemplatesGeneratedPage(
              resolvedIssueTemplates,
              collectIssueTemplateRecipeStubs(
                this.activeBundles,
                resolveIssueDefaults(this.options.issueDefaults),
              ),
            ).split("\n"),
          },
        );
      }
    }

    // Validate the temporal-framing convention. Empty path entries
    // and non-positive cadences fail the build before rendering so
    // agents never ship with a malformed temporal-framing contract.
    // The optional lint script is emitted here when the consumer
    // opts in via `emitChecker: true` — keeping the emission next to
    // validation keeps every temporal-framing side-effect in one
    // place.
    const resolvedTemporalFraming = validateTemporalFramingConfig(
      this.options.temporalFraming,
    );
    if (
      resolvedTemporalFraming.enabled &&
      resolvedTemporalFraming.emitChecker
    ) {
      new TextFile(this, ".claude/procedures/check-temporal-framing.sh", {
        lines: renderTemporalFramingCheckerScript(
          resolvedTemporalFraming,
        ).split("\n"),
        executable: true,
      });
    }

    const platforms = this.resolvePlatforms();
    const rules = this.resolveRules();
    const skills = this.resolveSkills();
    const subAgents = this.resolveSubAgents();
    const procedures = this.resolveProcedures();
    const commands = this.resolveCommands();
    const mcpServers = this.options.mcpServers ?? {};

    // Resolve template variables in rule content, skill instructions, and
    // sub-agent prompts using ProjectMetadata (if available).
    const projectMetadata = ProjectMetadata.of(this.project);
    const metadata = projectMetadata?.metadata;
    const resolvedRules = this.resolveTemplates(rules, metadata);
    const resolvedSkills = this.resolveSkillTemplates(skills, metadata);
    const resolvedSubAgents = this.resolveSubAgentTemplates(
      subAgents,
      metadata,
    );
    const resolvedProcedures = this.resolveProcedureTemplates(
      procedures,
      metadata,
    );

    if (platforms.includes(AGENT_PLATFORM.CURSOR)) {
      CursorRenderer.render(
        this,
        resolvedRules,
        resolvedSkills,
        resolvedSubAgents,
        mcpServers,
        this.options.cursorSettings,
      );
    }

    if (platforms.includes(AGENT_PLATFORM.CLAUDE)) {
      const bundlePermissions = this.resolveBundlePermissions();
      ClaudeRenderer.render(
        this,
        resolvedRules,
        resolvedSkills,
        resolvedSubAgents,
        mcpServers,
        AgentConfig.mergeClaudeDefaults(
          this.options.claudeSettings,
          bundlePermissions,
        ),
        resolvedProcedures,
        commands,
      );
    }

    if (platforms.includes(AGENT_PLATFORM.CODEX)) {
      CodexRenderer.render(
        this,
        resolvedRules,
        resolvedSkills,
        resolvedSubAgents,
      );
    }

    if (platforms.includes(AGENT_PLATFORM.COPILOT)) {
      CopilotRenderer.render(
        this,
        resolvedRules,
        resolvedSkills,
        resolvedSubAgents,
      );
    }
  }

  private resolvePlatforms(): ReadonlyArray<AgentPlatform> {
    return (
      this.options.platforms ?? [AGENT_PLATFORM.CURSOR, AGENT_PLATFORM.CLAUDE]
    );
  }

  private resolveRules(): AgentRule[] {
    const ruleMap = new Map<string, AgentRule>();

    // 1. Auto-detected bundle rules
    if (this.options.autoDetectBundles !== false) {
      for (const bundle of this.pathAwareBundles) {
        if (this.options.excludeBundles?.includes(bundle.name)) continue;
        if (bundle.name === "base" && this.options.includeBaseRules === false) {
          continue;
        }
        if (bundle.appliesWhen(this.project)) {
          for (const rule of this.bundleRulesFor(bundle)) {
            ruleMap.set(rule.name, rule);
          }
        }
      }
    }

    // 2. Force-included bundles
    if (this.options.includeBundles) {
      for (const bundleName of this.options.includeBundles) {
        const bundle = this.pathAwareBundles.find((b) => b.name === bundleName);
        if (bundle) {
          for (const rule of this.bundleRulesFor(bundle)) {
            ruleMap.set(rule.name, rule);
          }
        }
      }
    }

    // 3. Custom rules (override bundle rules with same name)
    if (this.options.rules) {
      for (const rule of this.options.rules) {
        ruleMap.set(rule.name, rule);
      }
    }

    // 4. Remove excluded rules
    if (this.options.excludeRules) {
      for (const name of this.options.excludeRules) {
        ruleMap.delete(name);
      }
    }

    // 5. Apply rule extensions (append content to existing rules)
    if (this.options.ruleExtensions) {
      for (const [name, extra] of Object.entries(this.options.ruleExtensions)) {
        const existing = ruleMap.get(name);
        if (existing) {
          ruleMap.set(name, {
            ...existing,
            content: `${existing.content}\n\n---\n\n${extra}`,
          });
        }
      }
    }

    // 5a. Apply additional rule paths (append filePatterns globs to
    // existing FILE_PATTERN-scoped rules). Silently no-op for
    // ALWAYS-scoped rules — see AgentConfigOptions.additionalRulePaths.
    if (this.options.additionalRulePaths) {
      for (const [name, extraPaths] of Object.entries(
        this.options.additionalRulePaths,
      )) {
        if (extraPaths.length === 0) continue;
        const existing = ruleMap.get(name);
        if (!existing) continue;
        if (existing.scope !== AGENT_RULE_SCOPE.FILE_PATTERN) continue;
        ruleMap.set(name, {
          ...existing,
          filePatterns: [...(existing.filePatterns ?? []), ...extraPaths],
        });
      }
    }

    // 5b. Append project-specific priority rules to issue-label-conventions
    if (this.options.priorityRules && this.options.priorityRules.length > 0) {
      const issueLabelRule = ruleMap.get("issue-label-conventions");
      if (issueLabelRule) {
        const section = renderPriorityRulesSection(this.options.priorityRules);
        ruleMap.set("issue-label-conventions", {
          ...issueLabelRule,
          content: `${issueLabelRule.content}\n\n---\n\n${section}`,
        });
      }
    }

    // 5c. Append focus-scoring section to issue-label-conventions
    if (this.options.focus) {
      const issueLabelRule = ruleMap.get("issue-label-conventions");
      if (issueLabelRule) {
        const section = renderFocusSection(this.options.focus);
        if (section.length > 0) {
          ruleMap.set("issue-label-conventions", {
            ...issueLabelRule,
            content: `${issueLabelRule.content}\n\n---\n\n${section}`,
          });
        }
      }
    }

    // 5d. Append meeting-type / area sections to
    // meeting-processing-workflow. The workflow rule itself is
    // excluded from the Claude platform output post-Batch-5 (the
    // routing summary lives in the shared agent-registry rule),
    // but the rule object is still mutated here so Cursor mode
    // continues to ship the meetings taxonomy. The standalone
    // `meetings-taxonomy` rule wired below picks up the same
    // section so CLAUDE.md keeps the consumer-supplied taxonomy
    // visible.
    if (this.options.meetings) {
      const meetingRule = ruleMap.get("meeting-processing-workflow");
      if (meetingRule) {
        const section = renderMeetingTypesSection(this.options.meetings);
        if (section.length > 0) {
          ruleMap.set("meeting-processing-workflow", {
            ...meetingRule,
            content: `${meetingRule.content}\n\n---\n\n${section}`,
          });
        }
      }
    }

    // NOTE: the `orchestrator-conventions`, `progress-file-convention`,
    // `shared-editing-safety`, `skill-evals`,
    // `issue-templates-convention`, and `temporal-framing-convention`
    // rules are **config-driven**: their bodies are rendered from the
    // consumer's resolved config. That rendering happens in the owning
    // bundle factory (see `resolvedRuleConventions` above), *before*
    // the rule map is seeded — never here.
    //
    // Do not add a late "rewrite rule X from resolved config" pass to
    // this method. Every pass below step 3 runs after custom rules
    // (step 3) and rule extensions (step 5) have already been merged,
    // so a wholesale content replacement discards them: that is
    // exactly how `ruleExtensions["orchestrator-conventions"]` came to
    // be silently dropped for any consumer who also set `scopeGate`.
    // Composition passes that *append* to a rule are fine — the
    // per-bundle convention hooks below are the canonical example.
    const excludedBundleNames = this.options.excludeBundles ?? [];

    // 5d2.5. Resolve the CLAUDE.md `injectBundleHooks` knob once for
    // the four hook-injection passes below. When the consumer opts
    // out (`claudeMd: { injectBundleHooks: false }`), each loop is
    // skipped and the per-bundle "see also" subsections are dropped
    // from every `<bundle>-workflow` rule. The convention rules
    // themselves still render unconditionally as standalone CLAUDE.md
    // sections — only the duplicated per-bundle stubs go away.
    const injectBundleHooks = this.options.claudeMd?.injectBundleHooks ?? true;

    // 5d3. Inject a short "Progress File" hook section into every
    // phased-agent bundle rule that references the convention. The
    // `progress-file-convention` rule itself is already rendered from
    // the consumer's resolved config by the base bundle. The hook is
    // injected whenever the convention is enabled so every phased
    // agent renders the reminder in its own workflow rule.
    const resolvedProgressFiles = resolveProgressFiles(
      this.options.progressFiles,
    );
    if (injectBundleHooks && resolvedProgressFiles.enabled) {
      for (const [ruleName, label] of PROGRESS_FILE_BUNDLE_HOOKS) {
        const existing = ruleMap.get(ruleName);
        if (!existing) {
          continue;
        }
        const hook = renderProgressFilesBundleHook(
          resolvedProgressFiles,
          label,
        );
        if (hook.length === 0) {
          continue;
        }
        ruleMap.set(ruleName, {
          ...existing,
          content: `${existing.content}\n\n---\n\n${hook}`,
        });
      }
    }

    // 5d4. Inject a short "Shared Index Editing" hook section into
    // every phased-agent bundle rule that writes rows to a shared
    // registry or feature-matrix file. The `shared-editing-safety`
    // rule itself is already rendered from the consumer's resolved
    // config by the base bundle.
    const resolvedSharedEditingForRules = resolveSharedEditing(
      this.options.sharedEditing,
    );
    if (injectBundleHooks && resolvedSharedEditingForRules.enabled) {
      for (const [ruleName, label] of SHARED_EDITING_BUNDLE_HOOKS) {
        const existing = ruleMap.get(ruleName);
        if (!existing) {
          continue;
        }
        const hook = renderSharedEditingBundleHook(
          resolvedSharedEditingForRules,
          label,
        );
        if (hook.length === 0) {
          continue;
        }
        ruleMap.set(ruleName, {
          ...existing,
          content: `${existing.content}\n\n---\n\n${hook}`,
        });
      }
    }

    // 5d5. Inject a short "Skill Evals" hook section into every
    // skill-owning bundle rule that ships a prompt-driven skill. The
    // `skill-evals` rule itself is already rendered from the
    // consumer's resolved config by the base bundle.
    const resolvedSkillEvalsForRules = resolveSkillEvals(
      this.options.skillEvals,
    );
    if (injectBundleHooks && resolvedSkillEvalsForRules.enabled) {
      for (const [ruleName, label] of SKILL_EVALS_BUNDLE_HOOKS) {
        const existing = ruleMap.get(ruleName);
        if (!existing) {
          continue;
        }
        const hook = renderSkillEvalsBundleHook(
          resolvedSkillEvalsForRules,
          label,
        );
        if (hook.length === 0) {
          continue;
        }
        ruleMap.set(ruleName, {
          ...existing,
          content: `${existing.content}\n\n---\n\n${hook}`,
        });
      }
    }

    // 5d7. Inject a short "Issue Templates" hook section into every
    // phased-agent bundle rule whose workflow dispatches downstream
    // issues. The `issue-templates-convention` rule itself is already
    // rendered from the consumer's resolved config by the base bundle
    // — including the disabled-stub variant it falls back to once
    // every downstream-issue-kind bundle has been excluded.
    const resolvedIssueTemplatesForRules = resolveIssueTemplates(
      this.options.issueTemplates,
    );
    const hasDownstreamBundles =
      hasAnyDownstreamIssueKindBundle(excludedBundleNames);
    if (
      injectBundleHooks &&
      resolvedIssueTemplatesForRules.enabled &&
      hasDownstreamBundles
    ) {
      for (const [ruleName, label] of ISSUE_TEMPLATES_BUNDLE_HOOKS) {
        const existing = ruleMap.get(ruleName);
        if (!existing) {
          continue;
        }
        const hook = renderIssueTemplatesBundleHook(
          resolvedIssueTemplatesForRules,
          label,
        );
        if (hook.length === 0) {
          continue;
        }
        ruleMap.set(ruleName, {
          ...existing,
          content: `${existing.content}\n\n---\n\n${hook}`,
        });
      }
    }

    // 5d8. (The `temporal-framing-convention` rule is rendered from
    // the consumer's resolved config by the base bundle; it has no
    // per-bundle hook, so nothing is done here.)

    // 5e. Gate the base bundle's `source-quality-verification` rule
    // behind `features.sourceTierExamples`. When the feature is absent
    // or every tier array is empty, drop the rule entirely so
    // pure-infra / CDK-only repos that never produce research don't
    // ship an empty tier stub. When the feature is active, inject the
    // consumer-supplied examples under the T1–T4 headings.
    const tierExamples = this.options.features?.sourceTierExamples;
    if (AgentConfig.hasActiveTierExamples(tierExamples)) {
      const sourceRule = ruleMap.get("source-quality-verification");
      if (sourceRule) {
        const updated = renderSourceTierExamples(
          sourceRule.content,
          tierExamples,
        );
        if (updated !== sourceRule.content) {
          ruleMap.set("source-quality-verification", {
            ...sourceRule,
            content: updated,
          });
        }
      }
    } else {
      ruleMap.delete("source-quality-verification");
    }

    // 5f. Gate the upstream-configulator-docs bundle behind
    // `upstreamConfigulator.enabled`. The bundle ships **enabled** by
    // default so every consumer of `@codedrifters/configulator`
    // carries the upstream pointers; non-CodeDrifters consumers opt
    // out by setting `upstreamConfigulator.enabled = false`, at which
    // point every rule whose name starts with
    // `upstream-configulator-docs` is filtered out of the resolved set.
    if (this.options.upstreamConfigulator?.enabled === false) {
      for (const ruleName of [...ruleMap.keys()]) {
        if (ruleName.startsWith("upstream-configulator-docs")) {
          ruleMap.delete(ruleName);
        }
      }
    }

    // 5g. Consolidate per-bundle workflow rules into a single
    // shared `agent-registry` rule. The registry renders one
    // alphabetical row per phased-agent bundle (skill, agent,
    // bundle, output path, purpose) so the routing signal stays
    // ALWAYS-scoped without 18 prose summaries each duplicating
    // the same handful of facts. Each `<bundle>-workflow` rule
    // is left in the rule map (so Cursor mode still sees it) but
    // marked `platforms.claude.exclude = true` so the Claude
    // platform output skips it. The convention rules at the top
    // of CLAUDE.md (Progress File, Shared-Editing Safety, Issue
    // Templates, Skill Evals) are unaffected.
    const registryRule = buildAgentRegistryRule(
      this.activeBundles,
      this.resolvedPaths,
    );
    if (registryRule) {
      // When the consumer supplies a `meetings` config AND the
      // meeting-analysis bundle is active, append the resolved
      // meeting-type / area subsections to the registry rule so
      // the consumer-supplied taxonomy stays visible in CLAUDE.md.
      // The same subsections are also injected into
      // `meeting-processing-workflow` above so Cursor mode (which
      // still renders that rule) sees the taxonomy. Excluding
      // meeting-analysis drops the registry's meetings appendix
      // because the taxonomy has nothing to reference.
      let registryContent = registryRule.content;
      const meetingAnalysisActive = this.activeBundles.some(
        (b) => b.name === "meeting-analysis",
      );
      if (this.options.meetings && meetingAnalysisActive) {
        const meetingsSection = renderMeetingTypesSection(
          this.options.meetings,
        );
        if (meetingsSection.length > 0) {
          registryContent = `${registryContent}\n\n---\n\n${meetingsSection}`;
        }
      }
      ruleMap.set(registryRule.name, {
        ...registryRule,
        content: registryContent,
      });

      // Bundle names that the consumer has targeted with one or
      // more `features.customDocSections` entries. Their workflow
      // rules carry consumer-supplied prose that the registry
      // table cannot capture, so they keep rendering to CLAUDE.md.
      // The registry row still appears for them — consumers see
      // the routing summary AND the per-bundle custom section.
      const customSectionBundles = new Set(
        (this.options.features?.customDocSections ?? []).map(
          (s) => s.bundleName,
        ),
      );

      for (const [ruleName, rule] of ruleMap) {
        if (!isSuppressedWorkflowRule(ruleName)) {
          continue;
        }
        // Workflow rules whose owning bundle carries a
        // customDocSection injection stay visible in CLAUDE.md
        // — the consumer-supplied prose has nowhere else to go.
        const owningBundleName = bundleNameForWorkflowRule(ruleName);
        if (
          owningBundleName !== undefined &&
          customSectionBundles.has(owningBundleName)
        ) {
          continue;
        }
        const existingClaude = rule.platforms?.claude ?? {};
        if (existingClaude.exclude === true) {
          continue;
        }
        ruleMap.set(ruleName, {
          ...rule,
          platforms: {
            ...rule.platforms,
            claude: { ...existingClaude, exclude: true },
          },
        });
      }
    }

    // 5h. Build the ALWAYS-scoped `conventions-registry` rule from
    // the final, post-transform rule map. The registry renders one
    // row per FILE_PATTERN-scoped rule (slug, file patterns,
    // description) so the Claude session sees a routing table back
    // into the full convention set even when its current file does
    // not match any rule's `paths:`. Built last so suppressed
    // workflow rules and customised content from earlier transforms
    // are reflected in the rendered table.
    const conventionsRegistryRule = buildConventionsRegistryRule(
      ruleMap.values(),
    );
    if (conventionsRegistryRule) {
      ruleMap.set(conventionsRegistryRule.name, conventionsRegistryRule);
    }

    // 6. Sort: project-overview first, then by first tag (alphabetical), then by name
    return [...ruleMap.values()].sort((a, b) => {
      if (a.name === "project-overview") return -1;
      if (b.name === "project-overview") return 1;

      const tagA = a.tags?.[0] ?? "\uffff";
      const tagB = b.tags?.[0] ?? "\uffff";
      if (tagA !== tagB) return tagA.localeCompare(tagB);

      return a.name.localeCompare(b.name);
    });
  }

  /**
   * Return a bundle's rules with `filePatterns` narrowed to the projects
   * that actually matched the bundle's detection predicate (when the bundle
   * provides `findApplicableProjects`) and any project-specified
   * `features.customDocSections` entries injected into the matching rule
   * content. Rules with `ALWAYS` scope and rules on bundles that don't
   * implement the hook are returned with only the custom-section
   * transformation (if any) applied.
   */
  private bundleRulesFor(bundle: AgentRuleBundle): ReadonlyArray<AgentRule> {
    const customSections = this.options.features?.customDocSections ?? [];
    const withCustomSections =
      customSections.length > 0
        ? renderCustomDocSections(bundle, customSections)
        : bundle;

    if (!withCustomSections.findApplicableProjects) {
      return withCustomSections.rules;
    }
    const detected = withCustomSections.findApplicableProjects(this.project);
    if (detected.length === 0) {
      return withCustomSections.rules;
    }
    const scoped = scopeFilePatternsToProjects(this.project, detected);
    return withCustomSections.rules.map((rule) =>
      rule.scope === AGENT_RULE_SCOPE.FILE_PATTERN
        ? { ...rule, filePatterns: scoped }
        : rule,
    );
  }

  private resolveSkills(): AgentSkill[] {
    const skillMap = new Map<string, AgentSkill>();

    // 1. Auto-detected bundle skills
    if (this.options.autoDetectBundles !== false) {
      for (const bundle of this.pathAwareBundles) {
        if (this.options.excludeBundles?.includes(bundle.name)) continue;
        if (bundle.appliesWhen(this.project) && bundle.skills) {
          for (const skill of bundle.skills) {
            skillMap.set(skill.name, skill);
          }
        }
      }
    }

    // 2. Force-included bundle skills
    if (this.options.includeBundles) {
      for (const bundleName of this.options.includeBundles) {
        const bundle = this.pathAwareBundles.find((b) => b.name === bundleName);
        if (bundle?.skills) {
          for (const skill of bundle.skills) {
            skillMap.set(skill.name, skill);
          }
        }
      }
    }

    // 3. Custom skills (override)
    if (this.options.skills) {
      for (const skill of this.options.skills) {
        skillMap.set(skill.name, skill);
      }
    }

    return [...skillMap.values()];
  }

  private resolveSubAgents(): AgentSubAgent[] {
    const agentMap = new Map<string, AgentSubAgent>();

    // 1. Auto-detected bundle sub-agents
    if (this.options.autoDetectBundles !== false) {
      for (const bundle of this.pathAwareBundles) {
        if (this.options.excludeBundles?.includes(bundle.name)) continue;
        if (bundle.appliesWhen(this.project) && bundle.subAgents) {
          for (const agent of bundle.subAgents) {
            agentMap.set(agent.name, agent);
          }
        }
      }
    }

    // 2. Force-included bundle sub-agents
    if (this.options.includeBundles) {
      for (const bundleName of this.options.includeBundles) {
        const bundle = this.pathAwareBundles.find((b) => b.name === bundleName);
        if (bundle?.subAgents) {
          for (const agent of bundle.subAgents) {
            agentMap.set(agent.name, agent);
          }
        }
      }
    }

    // 3. Custom sub-agents (override)
    if (this.options.subAgents) {
      for (const agent of this.options.subAgents) {
        agentMap.set(agent.name, agent);
      }
    }

    return [...agentMap.values()];
  }

  private resolveProcedures(): AgentProcedure[] {
    const procMap = new Map<string, AgentProcedure>();

    // 1. Auto-detected bundle procedures
    if (this.options.autoDetectBundles !== false) {
      for (const bundle of this.pathAwareBundles) {
        if (this.options.excludeBundles?.includes(bundle.name)) continue;
        if (bundle.appliesWhen(this.project) && bundle.procedures) {
          for (const proc of bundle.procedures) {
            procMap.set(proc.name, proc);
          }
        }
      }
    }

    // 2. Force-included bundle procedures
    if (this.options.includeBundles) {
      for (const bundleName of this.options.includeBundles) {
        const bundle = this.pathAwareBundles.find((b) => b.name === bundleName);
        if (bundle?.procedures) {
          for (const proc of bundle.procedures) {
            procMap.set(proc.name, proc);
          }
        }
      }
    }

    // 3. Custom procedures (override)
    if (this.options.procedures) {
      for (const proc of this.options.procedures) {
        procMap.set(proc.name, proc);
      }
    }

    // 4. Rewrite the orchestrator bundle's check-blocked.sh with the
    // consumer's resolved tier table, scope-gate settings, and
    // run-ratio settings. The procedure ships with defaults baked in,
    // so repos that never touch `options.tiers`, `options.scopeGate`,
    // or `options.runRatio` get the same generated script
    // byte-for-byte; this step only changes the emitted content when
    // the consumer overrides one of those configs.
    if (this.options.tiers || this.options.scopeGate || this.options.runRatio) {
      const existing = procMap.get("check-blocked.sh");
      if (existing) {
        const { procedure } = resolveOrchestratorAssets(
          this.options.tiers,
          this.options.scopeGate,
          this.options.runRatio,
        );
        if (procedure.content !== existing.content) {
          procMap.set("check-blocked.sh", procedure);
        }
      }
    }

    // 4b. Rewrite unblock-dependents.sh with the consumer's resolved
    // unblock-dependents settings. The procedure ships with defaults
    // baked in, so repos that never touch `options.unblockDependents`
    // see the same generated script byte-for-byte; this step only
    // changes the emitted content when the consumer overrides the
    // config.
    if (this.options.unblockDependents) {
      const existing = procMap.get("unblock-dependents.sh");
      if (existing) {
        const { unblockDependentsProcedure } = resolveOrchestratorAssets(
          undefined,
          undefined,
          undefined,
          undefined,
          this.options.unblockDependents,
        );
        if (unblockDependentsProcedure.content !== existing.content) {
          procMap.set("unblock-dependents.sh", unblockDependentsProcedure);
        }
      }
    }

    return [...procMap.values()];
  }

  /**
   * Resolves the final list of slash commands by merging bundle-shipped
   * defaults with consumer-supplied entries. Mirrors {@link resolveSkills}
   * and {@link resolveSubAgents}: auto-detected bundles contribute first,
   * force-included bundles overlay, and consumer commands override on
   * name collision. Names listed in `excludeCommands` are dropped after
   * the merge so consumers can opt out of a single default without
   * disabling the whole bundle.
   */
  private resolveCommands(): AgentCommand[] {
    const commandMap = new Map<string, AgentCommand>();

    // 1. Auto-detected bundle commands
    if (this.options.autoDetectBundles !== false) {
      for (const bundle of this.pathAwareBundles) {
        if (this.options.excludeBundles?.includes(bundle.name)) continue;
        if (bundle.appliesWhen(this.project) && bundle.commands) {
          for (const command of bundle.commands) {
            commandMap.set(command.name, command);
          }
        }
      }
    }

    // 2. Force-included bundle commands
    if (this.options.includeBundles) {
      for (const bundleName of this.options.includeBundles) {
        const bundle = this.pathAwareBundles.find((b) => b.name === bundleName);
        if (bundle?.commands) {
          for (const command of bundle.commands) {
            commandMap.set(command.name, command);
          }
        }
      }
    }

    // 3. Custom commands (override bundle commands with same name)
    if (this.options.commands) {
      for (const command of this.options.commands) {
        commandMap.set(command.name, command);
      }
    }

    // 4. Drop entries the consumer has explicitly excluded by name. This
    // runs last so a consumer override followed by an exclusion still
    // results in the command being dropped — predictable shadowing.
    if (this.options.excludeCommands) {
      for (const name of this.options.excludeCommands) {
        commandMap.delete(name);
      }
    }

    return [...commandMap.values()];
  }

  /**
   * Resolves template variables in rule content using project metadata.
   * Emits synthesis warnings for rules with unresolved variables.
   */
  private resolveTemplates(
    rules: ReadonlyArray<AgentRule>,
    metadata: ResolvedProjectMetadata | undefined,
  ): ReadonlyArray<AgentRule> {
    return rules.map((rule) => {
      const { resolved, unresolvedKeys } = resolveTemplateVariables(
        rule.content,
        metadata,
      );
      if (unresolvedKeys.length > 0) {
        this.project.logger.warn(
          `AgentConfig: ProjectMetadata not found; rule '${rule.name}' using default values`,
        );
      }
      return resolved !== rule.content ? { ...rule, content: resolved } : rule;
    });
  }

  /**
   * Resolves template variables in skill instructions using project metadata,
   * then appends any matching `skillExtensions` content after a horizontal
   * rule. Unknown keys in `skillExtensions` are silently ignored, mirroring
   * the `ruleExtensions` contract.
   */
  private resolveSkillTemplates(
    skills: ReadonlyArray<AgentSkill>,
    metadata: ResolvedProjectMetadata | undefined,
  ): ReadonlyArray<AgentSkill> {
    const extensions = this.options.skillExtensions;
    return skills.map((skill) => {
      const { resolved, unresolvedKeys } = resolveTemplateVariables(
        skill.instructions,
        metadata,
      );
      if (unresolvedKeys.length > 0) {
        this.project.logger.warn(
          `AgentConfig: ProjectMetadata not found; skill '${skill.name}' using default values`,
        );
      }
      const extra = extensions?.[skill.name];
      const baseInstructions = resolved;
      const finalInstructions =
        extra !== undefined && extra !== ""
          ? `${baseInstructions}\n\n---\n\n${extra}`
          : baseInstructions;
      return finalInstructions !== skill.instructions
        ? { ...skill, instructions: finalInstructions }
        : skill;
    });
  }

  /**
   * Resolves template variables in sub-agent prompts using project metadata,
   * then appends any matching `subAgentExtensions` content after a horizontal
   * rule. Unknown keys in `subAgentExtensions` are silently ignored,
   * mirroring the `ruleExtensions` contract.
   */
  private resolveSubAgentTemplates(
    subAgents: ReadonlyArray<AgentSubAgent>,
    metadata: ResolvedProjectMetadata | undefined,
  ): ReadonlyArray<AgentSubAgent> {
    const extensions = this.options.subAgentExtensions;
    return subAgents.map((agent) => {
      const { resolved, unresolvedKeys } = resolveTemplateVariables(
        agent.prompt,
        metadata,
      );
      if (unresolvedKeys.length > 0) {
        this.project.logger.warn(
          `AgentConfig: ProjectMetadata not found; sub-agent '${agent.name}' using default values`,
        );
      }
      const extra = extensions?.[agent.name];
      const basePrompt = resolved;
      const finalPrompt =
        extra !== undefined && extra !== ""
          ? `${basePrompt}\n\n---\n\n${extra}`
          : basePrompt;
      return finalPrompt !== agent.prompt
        ? { ...agent, prompt: finalPrompt }
        : agent;
    });
  }

  /**
   * Resolves template variables in procedure content using project metadata.
   */
  private resolveProcedureTemplates(
    procedures: ReadonlyArray<AgentProcedure>,
    metadata: ResolvedProjectMetadata | undefined,
  ): ReadonlyArray<AgentProcedure> {
    return procedures.map((proc) => {
      const { resolved, unresolvedKeys } = resolveTemplateVariables(
        proc.content,
        metadata,
      );
      if (unresolvedKeys.length > 0) {
        this.project.logger.warn(
          `AgentConfig: ProjectMetadata not found; procedure '${proc.name}' using default values`,
        );
      }
      return resolved !== proc.content ? { ...proc, content: resolved } : proc;
    });
  }

  /**
   * Collects Claude permission entries from all active bundles.
   */
  private resolveBundlePermissions(): {
    allow: Array<string>;
    deny: Array<string>;
  } {
    const allow: Array<string> = [];
    const deny: Array<string> = [];

    if (this.options.autoDetectBundles !== false) {
      for (const bundle of this.pathAwareBundles) {
        if (this.options.excludeBundles?.includes(bundle.name)) continue;
        if (bundle.appliesWhen(this.project) && bundle.claudePermissions) {
          if (bundle.claudePermissions.allow) {
            allow.push(...bundle.claudePermissions.allow);
          }
          if (bundle.claudePermissions.deny) {
            deny.push(...bundle.claudePermissions.deny);
          }
        }
      }
    }

    // Force-included bundles
    if (this.options.includeBundles) {
      for (const bundleName of this.options.includeBundles) {
        const bundle = this.pathAwareBundles.find((b) => b.name === bundleName);
        if (bundle?.claudePermissions) {
          if (bundle.claudePermissions.allow) {
            allow.push(...bundle.claudePermissions.allow);
          }
          if (bundle.claudePermissions.deny) {
            deny.push(...bundle.claudePermissions.deny);
          }
        }
      }
    }

    return { allow, deny };
  }
}
